/**
 * useAnalytics Hook
 * Professional analytics tracking hook for easy integration throughout the app
 */

import { useCallback } from 'react';
import {
  trackEvent,
  trackPageView,
  trackProductView,
  trackAddToCart,
  trackBeginCheckout,
  trackFormSubmission,
  trackContactFormSubmit,
  trackSearch,
  trackContactClick,
  trackMeasureToolUse,
  trackVehicleSearch,
  trackCartAbandonment,
  trackExitIntent,
  trackScrollDepth,
  trackBlogView,
  trackBlogRead,
} from '@/utils/analytics';

export interface UseAnalyticsReturn {
  // Core tracking functions
  track: (eventName: string, params?: Record<string, any>) => void;
  trackPage: (path: string, title: string) => void;

  // E-commerce tracking
  trackProductView: (product: any) => void;
  trackAddToCart: (product: any, quantity?: number) => void;
  trackBeginCheckout: (cartData: any) => void;
  trackCartAbandon: (cartData: any) => void;

  // User interaction tracking
  trackFormSubmit: (formName: string, formData?: Record<string, any>) => void;
  trackContactSubmit: () => void;
  trackSearch: (searchTerm: string) => void;
  trackContactClick: (type: 'phone' | 'email') => void;

  // Feature-specific tracking
  trackMeasureTool: () => void;
  trackVehicleSearch: (vehicleData: any) => void;

  // Engagement tracking
  trackExit: () => void;
  trackScroll: (percentage: number) => void;

  // Custom event tracking
  trackButtonClick: (buttonName: string, location?: string) => void;
  trackLinkClick: (linkText: string, url: string) => void;
  trackVideoPlay: (videoTitle: string) => void;
  trackFileDownload: (fileName: string, fileType: string) => void;
  trackNewsletterSignup: (location?: string) => void;
  trackSocialClick: (platform: string) => void;
  trackChatInteraction: (action: 'open' | 'close' | 'message') => void;
  trackFilterSelection: (filterType: string, filterValue: string) => void;
  trackCrossReferenceSearch: (brand: string, partNumber: string) => void;

  // Blog tracking
  trackBlogView: (blogData: { slug: string; title: string; category?: string; readTime?: string }) => void;
  trackBlogRead: (blogData: { slug: string; title: string; category?: string; timeSpent?: number }) => void;
}

/**
 * Custom hook for analytics tracking
 * Provides a clean interface for all analytics functions
 */
export function useAnalytics(): UseAnalyticsReturn {
  // Wrap core functions with useCallback for performance
  const track = useCallback((eventName: string, params?: Record<string, any>) => {
    trackEvent(eventName, params);
  }, []);

  const trackPage = useCallback((path: string, title: string) => {
    trackPageView(path, title);
  }, []);

  // E-commerce
  const handleTrackProductView = useCallback((product: any) => {
    trackProductView(product);
  }, []);

  const handleTrackAddToCart = useCallback((product: any, quantity = 1) => {
    trackAddToCart(product, quantity);
  }, []);

  const handleTrackBeginCheckout = useCallback((cartData: any) => {
    trackBeginCheckout(cartData);
  }, []);

  const trackCartAbandon = useCallback((cartData: any) => {
    trackCartAbandonment(cartData);
  }, []);

  // Forms and interactions
  const trackFormSubmit = useCallback((formName: string, formData?: Record<string, any>) => {
    trackFormSubmission(formName, formData);
  }, []);

  const trackContactSubmit = useCallback(() => {
    trackContactFormSubmit();
  }, []);

  const handleTrackSearch = useCallback((searchTerm: string) => {
    trackSearch(searchTerm);
  }, []);

  const handleTrackContactClick = useCallback((type: 'phone' | 'email') => {
    trackContactClick(type);
  }, []);

  // Features
  const trackMeasureTool = useCallback(() => {
    trackMeasureToolUse();
  }, []);

  const handleTrackVehicleSearch = useCallback((vehicleData: any) => {
    trackVehicleSearch(vehicleData);
  }, []);

  // Engagement
  const trackExit = useCallback(() => {
    trackExitIntent();
  }, []);

  const trackScroll = useCallback((percentage: number) => {
    trackScrollDepth(percentage);
  }, []);

  // Custom events
  const trackButtonClick = useCallback((buttonName: string, location?: string) => {
    track('button_click', {
      button_name: buttonName,
      page_location: location || (typeof window !== 'undefined' ? window.location.pathname : ''),
    });
  }, [track]);

  const trackLinkClick = useCallback((linkText: string, url: string) => {
    track('link_click', {
      link_text: linkText,
      link_url: url,
      page_location: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, [track]);

  const trackVideoPlay = useCallback((videoTitle: string) => {
    track('video_play', {
      video_title: videoTitle,
      page_location: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, [track]);

  const trackFileDownload = useCallback((fileName: string, fileType: string) => {
    track('file_download', {
      file_name: fileName,
      file_type: fileType,
      page_location: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, [track]);

  const trackNewsletterSignup = useCallback((location?: string) => {
    track('newsletter_signup', {
      signup_location: location || (typeof window !== 'undefined' ? window.location.pathname : ''),
    });
  }, [track]);

  const trackSocialClick = useCallback((platform: string) => {
    track('social_click', {
      social_platform: platform,
      page_location: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, [track]);

  const trackChatInteraction = useCallback((action: 'open' | 'close' | 'message') => {
    track('chat_interaction', {
      chat_action: action,
      page_location: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, [track]);

  const trackFilterSelection = useCallback((filterType: string, filterValue: string) => {
    track('filter_selection', {
      filter_type: filterType,
      filter_value: filterValue,
      page_location: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, [track]);

  const trackCrossReferenceSearch = useCallback((brand: string, partNumber: string) => {
    track('cross_reference_search', {
      competitor_brand: brand,
      part_number: partNumber,
      page_location: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, [track]);

  // Blog tracking
  const handleTrackBlogView = useCallback((blogData: { slug: string; title: string; category?: string; readTime?: string }) => {
    trackBlogView(blogData);
  }, []);

  const handleTrackBlogRead = useCallback((blogData: { slug: string; title: string; category?: string; timeSpent?: number }) => {
    trackBlogRead(blogData);
  }, []);

  return {
    track,
    trackPage,
    trackProductView: handleTrackProductView,
    trackAddToCart: handleTrackAddToCart,
    trackBeginCheckout: handleTrackBeginCheckout,
    trackCartAbandon,
    trackFormSubmit,
    trackContactSubmit,
    trackSearch: handleTrackSearch,
    trackContactClick: handleTrackContactClick,
    trackMeasureTool,
    trackVehicleSearch: handleTrackVehicleSearch,
    trackExit,
    trackScroll,
    trackButtonClick,
    trackLinkClick,
    trackVideoPlay,
    trackFileDownload,
    trackNewsletterSignup,
    trackSocialClick,
    trackChatInteraction,
    trackFilterSelection,
    trackCrossReferenceSearch,
    trackBlogView: handleTrackBlogView,
    trackBlogRead: handleTrackBlogRead,
  };
}
